import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Eye, Pencil, Trash2, Users, Calendar, DollarSign, Utensils, FileDown, X, Save, FolderOpen, Search } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#C4704B', '#8B5A3C', '#D4A574', '#A67C52', '#E8C4A0', '#6B4423', '#BFA084'];

export default function StaffMeals() {
  const { user } = useAuth();
  const [meals, setMeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [templateName, setTemplateName] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    meal_name: '',
    person_count: 1,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    ingredients: []
  });
  
  // Malzeme ekleme state
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const [mealsRes, productsRes, summaryRes, templatesRes] = await Promise.all([
        api.get('/staff-meals', { params }),
        api.get('/products'),
        api.get('/staff-meals/summary', { params }),
        api.get('/staff-meal-templates')
      ]);
      
      setMeals(mealsRes.data);
      setProducts(productsRes.data);
      setSummary(summaryRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIngredient = () => {
    if (!selectedProductId || !selectedQuantity || parseFloat(selectedQuantity) <= 0) {
      toast.error('Lütfen ürün seçin ve miktar girin');
      return;
    }
    
    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;
    
    // Aynı ürün zaten eklenmişse uyar
    if (formData.ingredients.find(i => i.product_id === selectedProductId)) {
      toast.error('Bu ürün zaten eklenmiş');
      return;
    }
    
    const newIngredient = {
      product_id: selectedProductId,
      product_name: product.name,
      quantity: parseFloat(selectedQuantity),
      unit: product.unit,
      unit_price: product.unit_price,
      total_cost: parseFloat(selectedQuantity) * product.unit_price
    };
    
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, newIngredient]
    }));
    
    setSelectedProductId('');
    setSelectedQuantity('');
  };

  const handleRemoveIngredient = (productId) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(i => i.product_id !== productId)
    }));
  };

  const handleLoadTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    // Şablondaki malzemeleri forma yükle
    const ingredientsWithCost = template.ingredients.map(ing => ({
      product_id: ing.product_id,
      product_name: ing.product_name,
      quantity: ing.quantity,
      unit: ing.unit,
      unit_price: ing.unit_price,
      total_cost: ing.quantity * ing.unit_price
    }));
    
    setFormData(prev => ({
      ...prev,
      meal_name: template.name,
      ingredients: ingredientsWithCost
    }));
    
    toast.success(`"${template.name}" şablonu yüklendi`);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Şablon adı girin');
      return;
    }
    if (formData.ingredients.length === 0) {
      toast.error('Şablon kaydetmek için en az bir malzeme ekleyin');
      return;
    }
    
    try {
      await api.post('/staff-meal-templates', {
        name: templateName,
        ingredients: formData.ingredients.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity
        }))
      });
      toast.success('Şablon kaydedildi');
      setIsTemplateDialogOpen(false);
      setTemplateName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablon kaydedilemedi');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/staff-meal-templates/${templateId}`);
      toast.success('Şablon silindi');
      fetchData();
    } catch (error) {
      toast.error('Şablon silinemedi');
    }
  };

  const handleCreate = async () => {
    if (!formData.meal_name.trim()) {
      toast.error('Lütfen yemek adı girin');
      return;
    }
    if (formData.ingredients.length === 0) {
      toast.error('En az bir malzeme eklemelisiniz');
      return;
    }
    if (formData.person_count < 1) {
      toast.error('Kişi sayısı en az 1 olmalıdır');
      return;
    }

    try {
      const payload = {
        meal_name: formData.meal_name,
        person_count: formData.person_count,
        date: formData.date,
        notes: formData.notes,
        ingredients: formData.ingredients.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity
        }))
      };
      
      await api.post('/staff-meals', payload);
      toast.success('Personel yemeği kaydedildi');
      setIsAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Personel yemeği eklenirken hata oluştu');
    }
  };

  const handleUpdate = async () => {
    if (!selectedMeal) return;

    try {
      await api.put(`/staff-meals/${selectedMeal.id}`, {
        notes: formData.notes,
        date: formData.date,
        meal_name: formData.meal_name
      });
      toast.success('Personel yemeği güncellendi');
      setIsEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu personel yemeğini silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await api.delete(`/staff-meals/${id}`);
      toast.success('Personel yemeği silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.get('/staff-meals/pdf/report', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `personel_yemegi_raporu_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF raporu indirildi');
    } catch (error) {
      toast.error('PDF raporu oluşturulurken hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      meal_name: '',
      person_count: 1,
      date: new Date().toISOString().split('T')[0],
      notes: '',
      ingredients: []
    });
    setSelectedProductId('');
    setSelectedQuantity('');
  };

  const openViewDialog = (meal) => {
    setSelectedMeal(meal);
    setIsViewDialogOpen(true);
  };

  const openEditDialog = (meal) => {
    setSelectedMeal(meal);
    setFormData({
      meal_name: meal.meal_name,
      person_count: meal.person_count,
      date: meal.date,
      notes: meal.notes || '',
      ingredients: meal.ingredients || []
    });
    setIsEditDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  // Toplam maliyet hesapla
  const calculateTotalCost = () => {
    return formData.ingredients.reduce((sum, ing) => sum + ing.total_cost, 0);
  };

  // Pie chart için veri hazırla
  const pieChartData = summary?.ingredient_report?.slice(0, 7).map((ing, index) => ({
    name: ing.product_name,
    value: ing.total_cost,
    percentage: summary.total_cost > 0 ? ((ing.total_cost / summary.total_cost) * 100).toFixed(1) : 0
  })) || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">Personel Yemeği</h1>
            <p className="text-muted-foreground mt-1">Personel yemek maliyet takibi ve analizi</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleExportPDF}
              data-testid="export-pdf-btn"
              disabled={meals.length === 0}
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF İndir
            </Button>
            
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-staff-meal-btn" onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Yemek Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Personel Yemeği Ekle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Şablon Seçimi */}
                  {templates.length > 0 && (
                    <div className="border rounded-lg p-4 bg-blue-50/50">
                      <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                        <FolderOpen className="w-4 h-4" />
                        Şablondan Yükle
                      </Label>
                      <div className="flex gap-2">
                        <Select onValueChange={handleLoadTemplate}>
                          <SelectTrigger data-testid="template-select">
                            <SelectValue placeholder="Şablon seçin..." />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((template) => (
                              <SelectItem key={template.id} value={template.id}>
                                {template.name} ({template.ingredients.length} malzeme)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  {/* Yemek Adı */}
                  <div>
                    <Label>Yemek Adı</Label>
                    <Input
                      value={formData.meal_name}
                      onChange={(e) => setFormData({ ...formData, meal_name: e.target.value })}
                      placeholder="Örn: Mercimek Çorbası, Pilav..."
                      data-testid="meal-name-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Kişi Sayısı</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.person_count}
                        onChange={(e) => setFormData({ ...formData, person_count: parseInt(e.target.value) || 1 })}
                        data-testid="person-count-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Tarih</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        data-testid="date-input"
                      />
                    </div>
                  </div>
                  
                  {/* Malzeme Ekleme */}
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <Label className="text-sm font-semibold mb-3 block">Malzeme Ekle</Label>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">Ürün</Label>
                        <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={productSearchOpen}
                              className="w-full justify-between font-normal h-10"
                              data-testid="product-select"
                            >
                              {selectedProductId ? (
                                <span className="truncate">
                                  {products.find(p => p.id === selectedProductId)?.name || 'Ürün seçildi'}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">Ürün ara...</span>
                              )}
                              <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[350px] p-0" align="start">
                            <Command
                              filter={(value, search) => {
                                const normalize = (str) => str
                                  .toLowerCase()
                                  .replace(/ı/g, 'i')
                                  .replace(/ğ/g, 'g')
                                  .replace(/ü/g, 'u')
                                  .replace(/ş/g, 's')
                                  .replace(/ö/g, 'o')
                                  .replace(/ç/g, 'c')
                                  .replace(/İ/g, 'i');
                                
                                const normalizedValue = normalize(value);
                                const normalizedSearch = normalize(search);
                                
                                if (normalizedValue.includes(normalizedSearch)) return 1;
                                return 0;
                              }}
                            >
                              <CommandInput 
                                placeholder="Ürün ara..." 
                                value={productSearchQuery}
                                onValueChange={setProductSearchQuery}
                              />
                              <CommandList>
                                <CommandEmpty>Sonuç bulunamadı</CommandEmpty>
                                <CommandGroup heading="🥩 Ürünler">
                                  {products.map((product) => (
                                    <CommandItem
                                      key={product.id}
                                      value={product.name}
                                      onSelect={() => {
                                        setSelectedProductId(product.id);
                                        setProductSearchQuery('');
                                        setProductSearchOpen(false);
                                      }}
                                      className="cursor-pointer"
                                    >
                                      <div className="flex items-center justify-between w-full">
                                        <div className="flex items-center gap-2">
                                          <span className="truncate">{product.name}</span>
                                          <span className="text-xs text-muted-foreground">({product.unit})</span>
                                        </div>
                                        <span className="text-xs font-medium text-muted-foreground">
                                          {formatCurrency(product.unit_price)}
                                        </span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="w-24">
                        <Label className="text-xs">Miktar</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={selectedQuantity}
                          onChange={(e) => setSelectedQuantity(e.target.value)}
                          placeholder="0.00"
                          data-testid="quantity-input"
                        />
                      </div>
                      <Button 
                        type="button" 
                        onClick={handleAddIngredient}
                        data-testid="add-ingredient-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Eklenen Malzemeler Listesi */}
                  {formData.ingredients.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Malzeme</TableHead>
                            <TableHead className="text-right">Miktar</TableHead>
                            <TableHead className="text-right">Birim Fiyat</TableHead>
                            <TableHead className="text-right">Toplam</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.ingredients.map((ing) => (
                            <TableRow key={ing.product_id}>
                              <TableCell className="font-medium">{ing.product_name}</TableCell>
                              <TableCell className="text-right">{ing.quantity} {ing.unit}</TableCell>
                              <TableCell className="text-right">{formatCurrency(ing.unit_price)}</TableCell>
                              <TableCell className="text-right font-semibold">{formatCurrency(ing.total_cost)}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveIngredient(ing.product_id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50">
                            <TableCell colSpan={3} className="font-semibold">Toplam Maliyet</TableCell>
                            <TableCell className="text-right font-bold text-lg">{formatCurrency(calculateTotalCost())}</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                          {formData.person_count > 0 && (
                            <TableRow className="bg-primary/10">
                              <TableCell colSpan={3} className="font-semibold">Kişi Başı Maliyet</TableCell>
                              <TableCell className="text-right font-bold text-lg text-primary">
                                {formatCurrency(calculateTotalCost() / formData.person_count)}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {/* Şablon Olarak Kaydet */}
                  {formData.ingredients.length > 0 && (
                    <div className="flex justify-end">
                      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="save-template-btn">
                            <Save className="w-4 h-4 mr-2" />
                            Şablon Olarak Kaydet
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Şablon Olarak Kaydet</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label>Şablon Adı</Label>
                              <Input
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                placeholder="Örn: Öğle Menüsü, Kahvaltı..."
                                data-testid="template-name-input"
                              />
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formData.ingredients.length} malzeme kaydedilecek
                            </div>
                            <Button onClick={handleSaveTemplate} className="w-full">
                              Şablonu Kaydet
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                  
                  <div>
                    <Label>Notlar (Opsiyonel)</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Ek notlar..."
                      data-testid="notes-input"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleCreate} 
                    className="w-full" 
                    data-testid="submit-staff-meal-btn"
                    disabled={formData.ingredients.length === 0 || !formData.meal_name.trim()}
                  >
                    Kaydet
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Utensils className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Toplam Öğün</p>
                    <p className="text-2xl font-bold">{summary.total_meals}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Toplam Kişi</p>
                    <p className="text-2xl font-bold">{summary.total_persons}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Toplam Maliyet</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_cost)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Calendar className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Kişi Başı Ortalama</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.avg_cost_per_person)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="list" className="space-y-4">
          <TabsList>
            <TabsTrigger value="list" data-testid="meals-list-tab">Yemek Listesi</TabsTrigger>
            <TabsTrigger value="report" data-testid="ingredient-report-tab">Malzeme Raporu</TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">Şablonlar</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label className="text-xs">Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                    data-testid="filter-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                    data-testid="filter-end-date"
                  />
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  data-testid="clear-filters-btn"
                >
                  Filtreleri Temizle
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Meals List Tab */}
          <TabsContent value="list">
            <Card>
              <CardHeader>
                <CardTitle>Yemek Listesi</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : meals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Henüz personel yemeği kaydı bulunmuyor
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Yemek</TableHead>
                        <TableHead className="text-center">Kişi</TableHead>
                        <TableHead className="text-right">Maliyet</TableHead>
                        <TableHead className="text-right">Kişi Başı</TableHead>
                        <TableHead className="text-center">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meals.map((meal) => (
                        <TableRow key={meal.id} data-testid={`meal-row-${meal.id}`}>
                          <TableCell>{formatDate(meal.date)}</TableCell>
                          <TableCell className="font-medium">{meal.meal_name}</TableCell>
                          <TableCell className="text-center">{meal.person_count}</TableCell>
                          <TableCell className="text-right">{formatCurrency(meal.total_cost)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(meal.cost_per_person)}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openViewDialog(meal)}
                                data-testid={`view-meal-${meal.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditDialog(meal)}
                                    data-testid={`edit-meal-${meal.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(meal.id)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-meal-${meal.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ingredient Report Tab */}
          <TabsContent value="report">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Malzeme Maliyet Dağılımı</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieChartData.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percentage }) => `${name} %${percentage}`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Veri bulunmuyor
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ingredient List */}
              <Card>
                <CardHeader>
                  <CardTitle>Malzeme Kullanım Detayı</CardTitle>
                </CardHeader>
                <CardContent>
                  {summary?.ingredient_report?.length > 0 ? (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {summary.ingredient_report.map((ing, index) => (
                        <div 
                          key={ing.product_id} 
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                          data-testid={`ingredient-report-${ing.product_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <div>
                              <p className="font-medium">{ing.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {ing.total_quantity.toFixed(2)} {ing.unit} • {ing.usage_count} kullanım
                              </p>
                            </div>
                          </div>
                          <p className="font-semibold">{formatCurrency(ing.total_cost)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Veri bulunmuyor
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle>Kayıtlı Şablonlar</CardTitle>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Henüz şablon kaydedilmemiş. Yemek eklerken "Şablon Olarak Kaydet" butonunu kullanabilirsiniz.
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates.map((template) => (
                      <Card key={template.id} className="relative">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-lg">{template.name}</CardTitle>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="text-destructive hover:text-destructive -mt-2 -mr-2"
                              data-testid={`delete-template-${template.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-1 text-sm">
                            {template.ingredients.slice(0, 3).map((ing) => (
                              <div key={ing.product_id} className="flex justify-between text-muted-foreground">
                                <span>{ing.product_name}</span>
                                <span>{ing.quantity} {ing.unit}</span>
                              </div>
                            ))}
                            {template.ingredients.length > 3 && (
                              <div className="text-muted-foreground text-xs">
                                +{template.ingredients.length - 3} malzeme daha
                              </div>
                            )}
                          </div>
                          <div className="mt-3 pt-3 border-t flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">
                              {template.ingredients.length} malzeme
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                handleLoadTemplate(template.id);
                                setIsAddDialogOpen(true);
                              }}
                              data-testid={`use-template-${template.id}`}
                            >
                              Kullan
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yemek Detayı</DialogTitle>
            </DialogHeader>
            {selectedMeal && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Yemek</p>
                    <p className="font-medium">{selectedMeal.meal_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tarih</p>
                    <p className="font-medium">{formatDate(selectedMeal.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Kişi Sayısı</p>
                    <p className="font-medium">{selectedMeal.person_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Maliyet</p>
                    <p className="font-medium">{formatCurrency(selectedMeal.total_cost)}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Kullanılan Malzemeler</p>
                  <div className="space-y-2">
                    {selectedMeal.ingredients.map((ing) => (
                      <div key={ing.product_id} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
                        <span>{ing.product_name}</span>
                        <span>{ing.quantity.toFixed(2)} {ing.unit} - {formatCurrency(ing.total_cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {selectedMeal.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notlar</p>
                    <p>{selectedMeal.notes}</p>
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground">
                  Oluşturan: {selectedMeal.created_by_name} • {new Date(selectedMeal.created_at).toLocaleString('tr-TR')}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yemek Düzenle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Yemek Adı</Label>
                <Input
                  value={formData.meal_name}
                  onChange={(e) => setFormData({ ...formData, meal_name: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Notlar</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar..."
                />
              </div>
              
              <Button onClick={handleUpdate} className="w-full">
                Güncelle
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
